'use client'

// components/Analytics/AnalyticsInit.jsx
//
// Arranca el cliente de telemetría anónima (utils/analytics.js) una sola vez
// y trackea la navegación: `section_view` cuando se abre una sección
// top-level (home/manager/wiki/blog/updates/export/editor/…) y `module_open`
// cuando se abre un módulo del editor (/editor/<módulo>). Todo pasa por el
// catch-all [[...slug]] con routing client-side, así que `usePathname()` es
// la única señal confiable de "cambió de sección" (no hay navegación server).
//
// Montado una sola vez en app/layout.providers.js, fuera de cualquier tab
// condicional — así corre para toda la vida de la SPA.

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { initAnalytics, track } from '@/utils/analytics'

function sectionFromPathname(pathname) {
    const clean = (pathname || '/').split('?')[0]
    const parts = clean.split('/').filter(Boolean)
    if (parts.length === 0) return { section: 'home', module: null }
    if (parts[0] === 'editor') return { section: 'editor', module: parts[1] || null }
    return { section: parts[0], module: null }
}

export default function AnalyticsInit() {
    const pathname = usePathname()
    const lastKeyRef = useRef(null)
    const initedRef = useRef(false)

    // El panel /admin es standalone (telemetría vista por nosotros, no por
    // usuarios) — no tiene sentido trackear nuestras propias visitas ahí.
    const isAdminPanel = (pathname || '').startsWith('/admin')

    useEffect(() => {
        if (isAdminPanel || initedRef.current) return
        initedRef.current = true
        initAnalytics()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdminPanel])

    useEffect(() => {
        if (isAdminPanel) return
        const { section, module } = sectionFromPathname(pathname)
        const key = `${section}:${module ?? ''}`
        if (lastKeyRef.current === key) return
        lastKeyRef.current = key

        track('section_view', { section })
        if (module) track('module_open', { module })
    }, [pathname])

    return null
}
